import { AfterContentInit, Component, ContentChildren, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ChangeDetectionStrategy } from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { from, Subject } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { ComingSoonUpsellService } from '../../../services/coming-soon-upsell/coming-soon-upsell.service';
import { SessionService } from '../../../services/session/session.service';
import { ComingSoonInterestEvent, ComingSoonInterestType } from './coming-soon-upsell.types';

@Component({
    selector: 'app-coming-soon-upsell',
    templateUrl: './coming-soon-upsell.component.html',
    styleUrls: ['./coming-soon-upsell.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ComingSoonUpsellComponent implements OnInit, AfterContentInit, OnDestroy {
  @Input() featureKey = 'unknown-feature';

  @Input() title = 'Coming Soon';
  @Input() description = 'This feature is on the way.';

  @Input() allowFeedback = true;
  @Input() renderMode: 'pill' | 'expanded' = 'pill';

  @Input() learnMoreLabel = 'Learn more';
  @Input() learnMoreHref: string | null = null;

  @Input() context: Record<string, unknown> | null = null;

  @Input() pillPosition: 'left' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'inline' = 'top-right';
  @Input() pillMode: 'absolute' | 'relative' = 'absolute';

  /** If true, details start open, but the feature is still disabled regardless */
  @Input() startOpen = false;

  @Output() interestSubmitted = new EventEmitter<{ success: boolean; payload: ComingSoonInterestEvent }>();

  isOpen = false;
  isSubmitted = false;

  isSubmitting = false;
  submitError: string | null = null;

  message = '';
  isCommenting = false;
  pendingInterestType: 'need' | 'curious' | null = null;

  @ContentChildren(MatSlideToggle, { descendants: true })
  private projectedSlideToggles?: QueryList<MatSlideToggle>;

  private readonly destroy$ = new Subject<void>();
  private resolvedEmail: string | null | undefined = undefined;
  private resolvingEmailPromise: Promise<string | undefined> | null = null;

  constructor(private comingSoonUpsellService: ComingSoonUpsellService, private sessionService: SessionService) {}

  ngOnInit(): void {
    if (this.renderMode === 'expanded' || this.startOpen) {
      this.isOpen = true;
    }

    void this.resolveEmail();
  }

  ngAfterContentInit(): void {
    this.disableProjectedSlideToggles();
    this.projectedSlideToggles?.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.disableProjectedSlideToggles();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleOpen(): void {
    if (this.renderMode === 'expanded') {
      return;
    }

    this.isOpen = !this.isOpen;

    if (!this.isOpen) {
      this.isCommenting = false;
      this.pendingInterestType = null;
      this.submitError = null;
    }
  }

  close(): void {
    if (this.renderMode === 'expanded') {
      return;
    }

    this.isOpen = false;
    this.isCommenting = false;
    this.pendingInterestType = null;
    this.submitError = null;
  }

  toggleCommenting(): void {
    this.isCommenting = !this.isCommenting;

    if (!this.isCommenting) {
      this.pendingInterestType = null;
      this.submitError = null;
    }
  }

  startInterestSubmission(type: 'need' | 'curious'): void {
    if (this.isSubmitting) {
      return;
    }

    this.submitError = null;

    if (!this.allowFeedback) {
      this.submit(type);
      return;
    }

    this.pendingInterestType = type;
    this.isCommenting = true;
  }

  submitCommenting(): void {
    this.submit(this.pendingInterestType || 'feedback');
  }

  cancelCommenting(): void {
    this.isCommenting = false;
    this.pendingInterestType = null;
    this.submitError = null;
  }

  get commentLabel(): string {
    if (this.pendingInterestType === 'curious') {
      return 'What made you choose "Not interested"?';
    }

    return 'What would you use this for?';
  }

  get commentPlaceholder(): string {
    return 'Optional feedback...';
  }

  get commentSubmitLabel(): string {
    if (this.isSubmitting) {
      return 'Submitting...';
    }

    if (this.pendingInterestType === 'need') {
      return 'Submit and prioritize';
    }

    if (this.pendingInterestType === 'curious') {
      return 'Submit response';
    }

    return 'Submit comments';
  }

  submit(type: ComingSoonInterestType): void {
    if (this.isSubmitting) {
      return;
    }

    this.submitError = null;

    this.isSubmitting = true;

    from(this.resolveEmail())
      .pipe(
        switchMap((email) => {
          const payload: ComingSoonInterestEvent = {
            featureKey: this.featureKey,
            type,
            email,
            message: this.allowFeedback ? (this.message || '').trim() || undefined : undefined,
            timestampIso: new Date().toISOString(),
            context: this.context ?? undefined,
          };

          return this.comingSoonUpsellService.submitInterest(payload).pipe(map((success) => ({ success, payload })));
        }),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe(({ success, payload }) => {
        if (success) {
          this.isSubmitted = true;
          this.isCommenting = false;
          this.pendingInterestType = null;
        } else {
          this.submitError = 'Something went wrong. Please try again.';
        }

        this.interestSubmitted.emit({ success, payload });
      });
  }

  private resolveEmail(): Promise<string | undefined> {
    if (this.resolvedEmail !== undefined) {
      return Promise.resolve(this.resolvedEmail || undefined);
    }

    if (this.resolvingEmailPromise) {
      return this.resolvingEmailPromise;
    }

    this.resolvingEmailPromise = this.sessionService
      .getUser()
      .then((user: any) => {
        this.resolvedEmail = this.extractEmail(user);
        return this.resolvedEmail || undefined;
      })
      .catch(() => {
        this.resolvedEmail = null;
        return undefined;
      })
      .finally(() => {
        this.resolvingEmailPromise = null;
      });

    return this.resolvingEmailPromise;
  }

  private extractEmail(user: any): string | null {
    const candidates = [
      user?.email,
      user?.user_email,
      user?.username,
      user?.info?.email,
      user?.attributes?.email,
      user?.user?.email,
    ];

    for (const candidate of candidates) {
      const value = `${candidate || ''}`.trim();
      if (this.isValidEmail(value)) {
        return value;
      }
    }

    return null;
  }

  private isValidEmail(value: string): boolean {
    return !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private disableProjectedSlideToggles(): void {
    this.projectedSlideToggles?.forEach((toggle) => {
      toggle.disabled = true;
    });
  }
}
